// One take of a song or a voiceover (live's shared `song-take-card`, which Pixar Singing Ad's and
// Claymation Ad's forms both show; spec G14-claymation-ad, PR d2): while made, its loader; then the
// radio that picks it (off, with the form's reason on hover, where the template cannot use it),
// the player, the verdict, a line of details, the fails and notes folded to one line, its error.
import { Group, Loader, Radio, Spoiler, Stack, Text, Tooltip } from "@mantine/core";

import type { PixarTakeVerdict, StillStatus } from "../../../api/generated/model";
import { AudioPlayer } from "../../../ui/AudioPlayer";
import classes from "./takes.module.css";

/** Live's words (`song-take-card/constants.ts`). */
const TAKE_RUNNING = "Making the take…";
const VERDICTS: Readonly<Record<PixarTakeVerdict, string>> = { pass: "Pass", note: "Pass with a note", fail: "Fail" };
/** One line of issues: a failed take can list every word of the script. */
const ISSUES_HEIGHT = 18;

/** A take as the server's draft views give it. */
export interface TakeView {
  number: number;
  status: StillStatus;
  url: string | null;
  duration_s: number | null;
  verdict: PixarTakeVerdict | null;
  fails: readonly string[];
  notes: readonly string[];
  problem: string | null;
  pickable: boolean;
}

export interface TakeCardProps {
  take: TakeView;
  picked: boolean;
  busy: boolean;
  /** Why a checked take cannot be picked, on its radio's hover. */
  unpickable: string;
  /** Under the player: a voiceover's last word and pace. */
  details: string | null;
  onPick: () => void;
}

export function TakeCard({ take, picked, busy, unpickable, details, onPick }: TakeCardProps) {
  const label = `Take ${take.number}`;
  if (take.status === "working" || take.status === "pending") {
    return (
      <Group gap={8} className={classes.take}>
        <Loader size="xs" aria-hidden />
        <Text className={classes.label}>{label}</Text>
        <Text className={classes.dimmed}>{TAKE_RUNNING}</Text>
      </Group>
    );
  }
  const issues = [...take.fails, ...take.notes];
  return (
    <Stack gap={4} className={classes.take} data-picked={picked || undefined}>
      <Group gap={8} wrap="nowrap">
        <Tooltip label={unpickable} multiline maw={280} withArrow disabled={take.verdict === null || take.pickable}>
          <span>
            <Radio checked={picked} disabled={busy || !take.pickable} aria-label={label} onChange={onPick} />
          </span>
        </Tooltip>
        <Text className={classes.label}>{label}</Text>
        {take.url === null ? null : <AudioPlayer url={take.url} name={label} durationSeconds={take.duration_s} seek />}
        {take.verdict === null ? null : (
          <Text className={classes.verdict} data-verdict={take.verdict}>
            {VERDICTS[take.verdict]}
          </Text>
        )}
      </Group>
      {details === null ? null : <Text className={classes.dimmed}>{details}</Text>}
      {issues.length === 0 ? null : (
        <Spoiler maxHeight={ISSUES_HEIGHT} showLabel="Show more" hideLabel="Show less">
          <Text className={classes.issues}>{issues.join("; ")}</Text>
        </Spoiler>
      )}
      {take.problem === null ? null : <Text className={classes.error}>{take.problem}</Text>}
    </Stack>
  );
}
